import React, { useEffect } from 'react';
import { useHistory, useParams, useLocation } from 'react-router-dom';
import { useLazyQuery } from '@apollo/client';
import { CircularProgress, ThemeProvider } from '@material-ui/core';
import { useFeatureFlagVariantKey, usePostHog } from 'posthog-js/react';

import { getTheme } from '@/utils/productHelpers';
import { getSupabaseClient } from '@/utils/supabaseClient';
import {
  REDIRECT_TO_SESSION_KEY,
  LOGIN_ORG_SLUG_PLACEHOLDER,
} from '@/utils/constants';
// eslint-disable-next-line max-len
import { appSuppliedRedirectTo } from '@/pages/PreOnboarding/steps/ActivateSleepio/hooks/usePostAuthRedirectTo';
import { buildRedirectParam, isAllowedAppHandoffTarget } from '@/utils/urlHelpers';
import { applyHandoffPayload } from '@/utils/handoffPayload';
import { logError } from '@/utils/logError';
import { ActiveFeatureFlag, isBooleanFeatureFlagEnabled } from '@/hooks/usePostHogFeatureFlags';
import { QUERY_DISTINCT_ID_WITH_EMAIL } from '@/apollo/queries';
import { linkPostHogIdentity } from '@/utils/posthog';

const SleepioEmailCallback: React.FC = () => {
  const history = useHistory();
  const { lob } = useParams<{ lob: string }>();
  const { search } = useLocation();
  const posthog = usePostHog();
  const [getDistinctId] = useLazyQuery(QUERY_DISTINCT_ID_WITH_EMAIL, { fetchPolicy: 'network-only' });
  const supabaseAuthEnabled = isBooleanFeatureFlagEnabled(
    useFeatureFlagVariantKey(ActiveFeatureFlag.CAP_USE_SUPABASE_AUTH),
  );

  useEffect(() => {
    const handleCallback = async () => {
      try {
        const params = new URLSearchParams(search);

        // Restores `redirectTo` if the caller wrapped it in `hp`. See handoffPayload.ts.
        applyHandoffPayload(params);
        const effectiveSearch = params.toString();

        const redirectToParam = params.get('redirectTo');
        // Only allowlisted handoff targets are saved; the later reader honours nothing else.
        if (redirectToParam && isAllowedAppHandoffTarget(redirectToParam)) {
          try {
            sessionStorage.setItem(REDIRECT_TO_SESSION_KEY, redirectToParam);
          } catch {
            // Locked-down storage throws; no error boundary here, so swallow it.
          }
        }

        await new Promise((resolve) => { setTimeout(resolve, 1500); });

        const { data: { session } } = await getSupabaseClient().auth.getSession();

        if (!session) {
          history.replace('/activate/sleepio/access-code', { error: 'email_verification_failed' });
          return;
        }

        const phDistinctId = session.user?.user_metadata?.phDistinctId;
        if (typeof phDistinctId === 'string' && phDistinctId) {
          posthog?.identify(phDistinctId);
        }

        if (supabaseAuthEnabled === null) { return; }

        if (!supabaseAuthEnabled) {
          history.replace('/activate/sleepio/employers');
          return;
        }

        // VAF's /api/auth/login sets `flow=login`; sign-up links don't.
        const isReturningUser = params.get('flow') === 'login';

        // Only sign-up links carry org_slug. Login gets a filler; its route ignores the org.
        const orgSlugParam = params.get('org_slug');
        const orgSlug = orgSlugParam || (isReturningUser ? LOGIN_ORG_SLUG_PLACEHOLDER : '');
        const intent = isReturningUser ? 'login' : 'sign-up';

        // A sign-up link without an org matches no route, so log it.
        if (!orgSlugParam && !isReturningUser) {
          logError('[SleepioEmailCallback]', 'sign-up callback has no org_slug; route will not match', undefined, {
            level: 'warning',
          });
        }

        // No default: a substituted address would read downstream as app-supplied.
        const redirectParam = buildRedirectParam(appSuppliedRedirectTo({ search: effectiveSearch }));

        const email = session.user?.email;
        if (email) {
          // Fire-and-forget: React Router navigation keeps the Apollo client alive,
          // so the in-flight query completes after the component unmounts.
          linkPostHogIdentity(getDistinctId, posthog, email, '[SleepioEmailCallback]');
        }

        history.replace(`/activate/sleepio-canopy/${lob}/${orgSlug}/${intent}/email/callback${redirectParam}`);
      } catch (err) {
        logError('[SleepioEmailCallback]', 'Unexpected error', err);
        history.replace('/activate/sleepio/access-code', { error: 'email_verification_failed' });
      }
    };

    handleCallback();
  }, [history, lob, search, supabaseAuthEnabled, getDistinctId, posthog]);

  return (
    <ThemeProvider theme={getTheme('sleepio')}>
      <div style={{
        display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh',
      }}
      >
        <CircularProgress color="primary" />
      </div>
    </ThemeProvider>
  );
};

export default SleepioEmailCallback;
